import * as React from 'react'
import { MoreHorizontal, Plus } from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { FormField } from '@/components/templates/entity-form'
import { Alert } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { deleteJSON, getJSON, patchJSON, postJSON } from '@/lib/api'
import { apiFieldMessages } from '@/lib/contacts/errors'
import { useViewOnly } from '@/components/templates/app-shell'

interface CustomPaymentType {
  id: number
  name: string
  archived: boolean
  used: boolean
}

interface CustomPaymentTypesResponse {
  custom_payment_types: CustomPaymentType[]
}

interface CustomPaymentTypeResponse {
  custom_payment_type: CustomPaymentType
}

type Action = { kind: 'rename' | 'archive' | 'reactivate' | 'delete'; type: CustomPaymentType }

export function CustomPaymentTypesSettings() {
  const viewOnly = useViewOnly()
  const queryClient = useQueryClient()
  const [name, setName] = React.useState('')
  const [action, setAction] = React.useState<Action | null>(null)
  const [editName, setEditName] = React.useState('')
  const typesQuery = useQuery({
    queryKey: ['custom-payment-types'],
    queryFn: () => getJSON<CustomPaymentTypesResponse>('/api/v1/custom_payment_types'),
    retry: false,
  })
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['custom-payment-types'] })
  const createType = useMutation({
    mutationFn: () =>
      postJSON<CustomPaymentTypeResponse>('/api/v1/custom_payment_types', {
        custom_payment_type: { name },
      }),
    onSuccess: async () => {
      setName('')
      await refresh()
    },
  })
  const changeType = useMutation({
    mutationFn: async () => {
      if (!action) return
      const path = `/api/v1/custom_payment_types/${action.type.id}`
      if (action.kind === 'delete') return deleteJSON(path)
      if (action.kind === 'rename') {
        return patchJSON<CustomPaymentTypeResponse>(path, {
          custom_payment_type: { name: editName },
        })
      }
      return patchJSON<CustomPaymentTypeResponse>(`${path}/${action.kind}`, {})
    },
    onSuccess: async () => {
      setAction(null)
      await refresh()
    },
  })
  const openAction = (next: Action) => {
    setAction(next)
    setEditName(next.type.name)
    changeType.reset()
  }
  const types = typesQuery.data?.custom_payment_types ?? []

  return (
    <section className="border-t border-border p-4">
      <div className="pb-3">
        <h2 className="text-body font-semibold text-foreground">Custom Payment Types</h2>
        <p className="pt-1 text-caption text-muted-foreground">
          Add offline methods such as Venmo or Square POS. Built-in cash, check, wire, other, and
          card stay available.
        </p>
      </div>
      {!viewOnly && (
        <form
          className="flex max-w-lg items-end gap-2 pb-4"
          onSubmit={(event) => {
            event.preventDefault()
            createType.mutate()
          }}
        >
          <FormField
            label="Payment Type name"
            htmlFor="custom-payment-type-name"
            error={apiFieldMessages(createType.error, 'name')[0]}
          >
            <Input
              id="custom-payment-type-name"
              className="h-9"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </FormField>
          <Button type="submit" size="sm" disabled={createType.isPending}>
            <Plus className="h-4 w-4" />
            {createType.isPending ? 'Adding...' : 'Add Payment Type'}
          </Button>
        </form>
      )}

      {typesQuery.isLoading ? (
        <p className="text-body-sm text-muted-foreground" role="status">
          Loading Payment Types...
        </p>
      ) : typesQuery.isError ? (
        <Alert>Custom Payment Types unavailable</Alert>
      ) : types.length === 0 ? (
        <p className="text-body-sm text-muted-foreground">No custom Payment Types yet</p>
      ) : (
        <PaymentTypesTable types={types} onAction={openAction} viewOnly={viewOnly} />
      )}

      <PaymentTypeActionDialog
        action={action}
        name={editName}
        error={changeType.error}
        pending={changeType.isPending}
        onNameChange={setEditName}
        onClose={() => setAction(null)}
        onConfirm={() => changeType.mutate()}
      />
    </section>
  )
}

function PaymentTypesTable({
  types,
  onAction,
  viewOnly,
}: {
  types: CustomPaymentType[]
  onAction: (action: Action) => void
  viewOnly: boolean
}) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="w-12">
            <span className="sr-only">Actions</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {types.map((type) => (
          <TableRow key={type.id}>
            <TableCell className="font-medium">{type.name}</TableCell>
            <TableCell>
              <Badge variant={type.archived ? 'neutral' : 'success'}>
                {type.archived ? 'Archived' : 'Active'}
              </Badge>
            </TableCell>
            <TableCell>
              {!viewOnly && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 w-8 p-0"
                      aria-label={`Actions for ${type.name}`}
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {!type.used && (
                      <DropdownMenuItem onSelect={() => onAction({ kind: 'rename', type })}>
                        Rename
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem
                      onSelect={() =>
                        onAction({ kind: type.archived ? 'reactivate' : 'archive', type })
                      }
                    >
                      {type.archived ? 'Reactivate' : 'Archive'}
                    </DropdownMenuItem>
                    {!type.used && (
                      <DropdownMenuItem onSelect={() => onAction({ kind: 'delete', type })}>
                        Delete
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

function PaymentTypeActionDialog({
  action,
  name,
  error,
  pending,
  onNameChange,
  onClose,
  onConfirm,
}: {
  action: Action | null
  name: string
  error: Error | null
  pending: boolean
  onNameChange: (name: string) => void
  onClose: () => void
  onConfirm: () => void
}) {
  if (!action) return null
  const title = `${action.kind === 'rename' ? 'Rename' : action.kind === 'delete' ? 'Delete' : action.kind === 'archive' ? 'Archive' : 'Reactivate'} ${action.type.name}`
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{actionDescription(action)}</DialogDescription>
        </DialogHeader>
        {action.kind === 'rename' && (
          <FormField
            label="Payment Type name"
            htmlFor="rename-payment-type"
            error={apiFieldMessages(error, 'name')[0]}
          >
            <Input
              id="rename-payment-type"
              className="h-9"
              value={name}
              onChange={(event) => onNameChange(event.target.value)}
            />
          </FormField>
        )}
        {action.kind !== 'rename' && error && (
          <Alert>{apiFieldMessages(error, 'base')[0] ?? 'Payment Type could not be changed'}</Alert>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant={action.kind === 'delete' ? 'destructive' : 'default'}
            disabled={pending}
            onClick={onConfirm}
          >
            {pending ? 'Saving...' : 'Confirm'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function actionDescription(action: Action) {
  if (action.kind === 'delete') return 'This permanently removes an unused Payment Type.'
  if (action.kind === 'archive')
    return 'Archived types stay on history but are hidden from new Payments.'
  if (action.kind === 'reactivate')
    return 'This makes the Payment Type available for new Payments again.'
  return 'Names can only be changed before the Payment Type has been used.'
}
